/* eslint-disable camelcase */
import { useMemo } from 'react';

import ProgramChoiceCard from '../../../../../components/minside/ProgramTab/ProgramChoiceCard';
import ProgramDaysNav from '../../../../../components/minside/ProgramTab/ProgramDaysNav';
import ProgramFeedback from '../../../../../components/minside/ProgramTab/ProgramFeedback';

import { useProgramSelection } from '../../../../../hooks/useProgramSelection';

import { formatDayLabel, getTodayYyyymmddLocal } from '../../../../../utils/formatDate';
import {
    arraysEqualAsSets,
    finnForsteAktivitetsDato,
    hentAktiviteterPerDato
} from '../../../../../utils/minside/activities';

import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

function ProgramTab({
    aktiviteter = [],
    aktivitetPameldApent = 'J',
    loading,
    onSaveDay,
    defaultActiveDato = null
}) {
    const aktiviteterPerDato = useMemo(() => hentAktiviteterPerDato(aktiviteter), [aktiviteter]);

    const datoer = useMemo(
        () => Object.keys(aktiviteterPerDato).sort((a, b) => Number(a) - Number(b)),
        [aktiviteterPerDato]
    );

    const initialSelectionByDate = useMemo(() => {
        return Object.fromEntries(
            Object.entries(aktiviteterPerDato).map(([dato, aktiviteterForDato]) => [
                dato,
                aktiviteterForDato.filter(a => a.pameldt === 'J').map(a => a.akt_id)
            ])
        );
    }, [aktiviteterPerDato]);

    const aktiviteterErApne = aktivitetPameldApent === 'J';

    const forsteAktivitetsDato = useMemo(
        () => finnForsteAktivitetsDato(aktiviteter),
        [aktiviteter]
    );

    const dagensDato = getTodayYyyymmddLocal();

    const visPameldingStengtInfo =
        !aktiviteterErApne && forsteAktivitetsDato !== null && forsteAktivitetsDato > dagensDato;

    const {
        activeDato,
        setActiveDato,
        draftByDate,
        savingByDate,
        feedbackByDate,
        feedbackVisibleByDate,
        feedbackRef,
        toggleAktivitet,
        saveDag
    } = useProgramSelection({
        datoer,
        initialSelectionByDate,
        defaultActiveDato,
        aktiviteterErApne,
        onSaveDay
    });

    if (loading) {
        return (
            <div className="data-loader">
                <FontAwesomeIcon icon={faSpinner} spin size="xl" />
            </div>
        );
    }

    if (!datoer.length) {
        return (
            <div className="program-tab">
                <section className="program-section">
                    <h2 className="program-section__heading">Aktiviteter</h2>
                    <p className="program-section__desc">
                        Det finnes ingen tilgjengelige aktiviteter akkurat nå.
                    </p>
                </section>
            </div>
        );
    }

    const aktivDato = String(activeDato);
    const aktiviteterForAktivDato = aktiviteterPerDato[aktivDato] ?? [];
    const checkedIds = draftByDate[aktivDato] ?? [];
    const saving = Boolean(savingByDate[aktivDato]);
    const feedback = feedbackByDate[aktivDato] ?? null;
    const feedbackVisible = Boolean(feedbackVisibleByDate[aktivDato]);

    const initialCheckedIds = aktiviteterForAktivDato
        .filter(a => a.pameldt === 'J')
        .map(a => a.akt_id);

    const isDirty = !arraysEqualAsSets(initialCheckedIds, checkedIds);

    return (
        <div className="program-tab">
            <section className="program-section">
                <h2 className="program-section__heading">Aktiviteter</h2>
                <p className="program-section__desc">
                    {aktiviteterErApne
                        ? 'Velg hvilke aktiviteter du ønsker å delta på. Du lagrer dag for dag.'
                        : 'Påmeldingen til aktiviteter er nå stengt. Du kan fortsatt se aktivitetene du er registrert på.'}
                </p>

                <ProgramDaysNav
                    datoer={datoer}
                    activeDato={aktivDato}
                    onSelectDato={setActiveDato}
                />

                <ProgramFeedback
                    feedback={feedback}
                    feedbackVisible={feedbackVisible}
                    aktiviteterErApne={aktiviteterErApne}
                    visPameldingStengtInfo={visPameldingStengtInfo}
                    feedbackRef={feedbackRef}
                />

                <div className="program-day__header">
                    <div className="program-day__heading">
                        <h3 className="program-day__title">{formatDayLabel(Number(aktivDato))}</h3>
                        <p className="program-day__sub">
                            {aktiviteterErApne
                                ? 'Velg aktivitetene du ønsker å delta på denne dagen.'
                                : 'Dette er aktivitetene for denne dagen.'}
                        </p>
                    </div>

                    {aktiviteterErApne && (
                        <div className="program-day__header-action">
                            <button
                                type="button"
                                className="btn-primary btn-small"
                                onClick={() => saveDag(aktivDato)}
                                disabled={saving || !isDirty}
                            >
                                {saving ? 'Lagrer...' : 'Lagre valg'}
                            </button>
                        </div>
                    )}
                </div>

                <div className="program-choice-grid">
                    {aktiviteterForAktivDato
                        .slice()
                        .sort((a, b) => (a.fratid ?? '').localeCompare(b.fratid ?? ''))
                        .map(aktivitet => {
                            const erFull = aktivitet.stengt === 'J';
                            const erChecked = checkedIds.includes(aktivitet.akt_id);

                            return (
                                <ProgramChoiceCard
                                    key={aktivitet.akt_id}
                                    aktivitet={aktivitet}
                                    checked={erChecked}
                                    onToggle={aktId => toggleAktivitet(aktivDato, aktId)}
                                    disabled={
                                        saving || !aktiviteterErApne || (erFull && !erChecked)
                                    }
                                    erFull={erFull}
                                />
                            );
                        })}
                </div>
            </section>
        </div>
    );
}

export default ProgramTab;
